import React, { useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  useTheme,
  alpha,
  Chip,
  Stack,
} from "@mui/material";
import { Droppable } from "@hello-pangea/dnd";
import { Search, PersonOff, People } from "@mui/icons-material";
import { useTranslate } from "@refinedev/core";
import { DnDUserCard, DnDUser } from "./DnDUserCard";

interface DnDUserColumnProps {
  droppableId: string;
  title: string;
  users: DnDUser[];
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedUserIds: Set<number>;
  onSelectUser: (userId: number, event: React.MouseEvent) => void;
  emptyMessage: string;
  emptyIcon?: React.ReactNode;
  isDragDisabled?: boolean;
  highlightColor?: "primary" | "success" | "warning";
  onPromote?: (userId: number) => Promise<void>;
  onDemote?: (userId: number) => Promise<void>;
  showRoleActions?: boolean;
}

export const DnDUserColumn: React.FC<DnDUserColumnProps> = ({
  droppableId,
  title,
  users,
  searchQuery,
  onSearchChange,
  selectedUserIds,
  onSelectUser,
  emptyMessage,
  emptyIcon,
  isDragDisabled = false,
  highlightColor = "primary",
  onPromote,
  onDemote,
  showRoleActions = false,
}) => {
  const theme = useTheme();
  const translate = useTranslate();

  // Filter users based on search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const query = searchQuery.toLowerCase();
    return users.filter(
      (u) =>
        u.fullname?.toLowerCase().includes(query) ||
        u.email.toLowerCase().includes(query)
    );
  }, [users, searchQuery]);

  const selectedCount = useMemo(() => {
    return filteredUsers.filter((u) => selectedUserIds.has(u.id)).length;
  }, [filteredUsers, selectedUserIds]);

  const getHighlightColors = () => {
    const colors = {
      primary: {
        light: theme.palette.primary.light,
        main: theme.palette.primary.main,
        dark: theme.palette.primary.dark,
      },
      success: {
        light: theme.palette.success.light,
        main: theme.palette.success.main,
        dark: theme.palette.success.dark,
      },
      warning: {
        light: theme.palette.warning.light,
        main: theme.palette.warning.main,
        dark: theme.palette.warning.dark,
      },
    };
    return colors[highlightColor];
  };

  const colors = getHighlightColors();

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        height: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
          bgcolor: alpha(colors.main, 0.04),
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="subtitle1" fontWeight={600} color={colors.dark}>
            {title}
          </Typography>
          <Chip
            icon={<People sx={{ fontSize: 16 }} />}
            label={`${filteredUsers.length}${searchQuery ? ` / ${users.length}` : ""}`}
            size="small"
            sx={{
              bgcolor: alpha(colors.main, 0.1),
              color: colors.dark,
              fontWeight: 500,
              "& .MuiChip-icon": {
                color: colors.main,
              },
            }}
          />
        </Stack>
      </Box>

      {/* Search */}
      <Box sx={{ p: 1.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}` }}>
        <TextField
          placeholder={translate("search.placeholder", "Rechercher...")}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 20, color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              bgcolor: theme.palette.background.paper,
              borderRadius: 2,
              "& fieldset": {
                borderColor: alpha(theme.palette.divider, 0.3),
              },
              "&:hover fieldset": {
                borderColor: alpha(colors.main, 0.5),
              },
              "&.Mui-focused fieldset": {
                borderColor: colors.main,
              },
            },
          }}
        />
      </Box>

      {/* Droppable Area */}
      <Droppable droppableId={droppableId} isDropDisabled={isDragDisabled}>
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              flex: 1,
              p: 1.5,
              overflowY: "auto",
              bgcolor: snapshot.isDraggingOver
                ? alpha(colors.main, 0.08)
                : "transparent",
              border: snapshot.isDraggingOver
                ? `2px dashed ${colors.main}`
                : "2px dashed transparent",
              borderRadius: 2,
              m: 1,
              transition: "all 0.2s ease",
              minHeight: 200,
            }}
          >
            {filteredUsers.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  minHeight: 200,
                  color: "text.secondary",
                }}
              >
                {emptyIcon || <PersonOff sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />}
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  {emptyMessage}
                </Typography>
              </Box>
            ) : (
              filteredUsers.map((user, index) => (
                <DnDUserCard
                  key={user.id}
                  user={user}
                  index={index}
                  isSelected={selectedUserIds.has(user.id)}
                  selectedCount={selectedCount}
                  onSelect={onSelectUser}
                  onPromote={onPromote}
                  onDemote={onDemote}
                  showRoleActions={showRoleActions}
                />
              ))
            )}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>

      {/* Selection indicator */}
      {selectedCount > 0 && (
        <Box
          sx={{
            px: 2,
            py: 1,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
            bgcolor: alpha(colors.main, 0.05),
          }}
        >
          <Typography variant="caption" color={colors.dark} fontWeight={500}>
            {selectedCount} {translate("subGroups.selected", "sélectionné(s)")}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DnDUserColumn;
